import * as React from 'react';
import { useState, useContext } from 'react';
import Button from '@mui/material/Button';
import { SvgIcon } from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageIcon from '@mui/icons-material/Image';
import AttachmentIcon from '@mui/icons-material/Attachment';
import IconButton from '@mui/material/IconButton';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTranslation } from "react-i18next";
import { useSnackbar } from 'notistack';
import { alpha } from '@mui/material/styles';
import { formatSizeToGb } from '../utils/fileManagement';
import { fileExistsInS3 } from "../utils/interfaceS3";
import { AuthContext } from "../Auth/AuthContext";
import { FileUploader } from "react-drag-drop-files";

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ProcessingInfoDialog from './processing-help-dialog';


import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';

export default function NewDataDialog({
  setOpen,
  open,
  projectSelected,
  userId,
  reloadProjects,
  addToUploadQueue,
  GoUploadedThisMonth,
  maxGoUploadPerMonth,
  processings, // <-- This prop contains the fetched processing data.
}) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { maxFileSize, user, idToken, idpool } = useContext(AuthContext);

  const [fileSelected, setFileSelected] = useState("");
  const [description, setDescription] = useState("");
  const [imageSelected, setImageSelected] = useState("");
  // Controlled selection state for the tree view.
  const [selectedItems, setSelectedItems] = useState([]);
  // Controlled expansion state for the tree view.
  const [expandedItems, setExpandedItems] = useState([]);

  const fileTypes = ["LAS", "LAZ", "las", "laz"];
  const handleChange = (file) => {
    setFileSelected(file);
  };

  const imageTypes = ["PNG", "png", "JPG", "jpg", "JPEG", "jpeg"];
  const handleImageChange = (file) => {
    setImageSelected(file);
  };

  const handleTypeError = () => {
    enqueueSnackbar(`${t("ErrorType")}`, { variant: "warning" });
  };

  const handleClose = () => {
    setOpen(false);
    clearFields();
  };

  function hasWhiteSpace(s) {
    return s.indexOf(" ") >= 0;
  }

  async function fileAlreadyExists(filename) {
    const fileExists = await fileExistsInS3(
      user.sub,
      idToken,
      `${global.REGION}`,
      idpool,
      filename
    );
    return fileExists;
  }

  function clearFields() {
    setFileSelected("");
    setImageSelected("");
    setDescription("");
    setSelectedItems([]);
    setExpandedItems([]);
  }

function extractIdsByTitles(inputTitles, processings) {
  const titleSet = new Set(inputTitles);
  const result = [];

  processings.forEach(proc => {
    // Check if the parent processing is selected.
    const processingSelected = titleSet.has(proc.title);
    
    // Get the IDs of all subprocessings that are selected.
    const selectedSubIds = (proc.subProcessings || [])
      .filter(sub => titleSet.has(sub.title))
      .map(sub => sub.id);

    // If either the processing or at least one subprocessing is selected,
    // include this processing in the result.
    if (processingSelected || selectedSubIds.length > 0) {
      result.push({
        processing: proc.id,
        subprocessings: selectedSubIds,
      });
    }
  });

  return result;
}

  const handleCreate = async () => {
    if (!fileSelected) {
      enqueueSnackbar(`${t("ErrorNoFile")}`, { variant: "warning" });
      return;
    }
    const exists = await fileAlreadyExists(fileSelected.name);
    if (exists) {
      enqueueSnackbar(`${t("ErrorAlreadyExists")}`, { variant: "warning" });
      clearFields();
      return;
    }
    if (hasWhiteSpace(fileSelected.name)) {
      enqueueSnackbar(`${t("ErrorFileNoSpace")}`, { variant: "warning" });
      clearFields();
      return;
    }
    if ((+GoUploadedThisMonth + +formatSizeToGb(fileSelected.size)) > maxGoUploadPerMonth) {
      enqueueSnackbar(`${t("MaxUploadReached")}`, { variant: "warning" });
      clearFields();
      return;
    }

    const processingList = extractIdsByTitles(selectedItems,processings);
    //console.log(processingList);
    //console.log('Subprocessing IDs:', subProcessingIds);

    const uploadItem = {
      file: fileSelected,
      imageFile: imageSelected,
      description: description,
      processings: processingList
    };

    addToUploadQueue([uploadItem]);
    setOpen(false);
    clearFields();
  };

  // Controlled selection handler.
  const handleSelectedItemsChange = (event, ids) => {
    let newSelected = [...ids];
    let newExpanded = [...ids];

    if (
      newSelected.includes("forest-report") &&
      !newExpanded.some((item) => item.includes("forest-report"))
    ) {
      newExpanded.push("forest-report");
    }

    if (selectedItems.includes("forest-report") && !newSelected.includes("forest-report")) {
      newSelected = newSelected.filter((item) => !item.startsWith("forest-report-"));
    }

    setExpandedItems(newExpanded);
    setSelectedItems(newSelected);
  };

  // Helper function to create a valid itemId from a title.
  const createItemId = (title) =>
    title.toLowerCase().replace(/\s+/g, '-');


  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [currentProcessing, setCurrentProcessing] = useState(null);

  const handleOpenInfo = proc => {
    setCurrentProcessing(proc);
    setInfoDialogOpen(true);
  };
  const handleCloseInfo = () => {
    setInfoDialogOpen(false);
    setCurrentProcessing(null);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Box sx={{ p: 3 }}>
        <DialogTitle>
          <Typography variant="h4" component="div">
            {t("UploadData")}
          </Typography>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={(theme) => ({
              position: 'absolute',
              right: 16,
              top: 16,
              color: theme.palette.grey[500],
          })}
          >
          <CancelIcon />
          </IconButton>
        <DialogContent>
          <DialogContentText component="div">
            <Typography color="text.secondary" variant="body2">
              {t("SelectALASFile")}
            </Typography>
          </DialogContentText>
          <Stack spacing={2} sx={{ mt: 3 }}>
            {/* File Uploader for LAS/LAZ Files */}
            <Stack spacing={1}>
              <Typography color="text.secondary" variant="h6">
                {t("LASLAZFile")}
              </Typography>
              <FileUploader
                handleChange={handleChange}
                onTypeError={handleTypeError}
                multiple={false}
                name="file"
                types={fileTypes}
                hoverTitle=" "
                maxSize={maxFileSize * 1000}
                children={
                  <Box>
                    <Paper
                      variant="outlined"
                      sx={{
                        borderColor: "#E5E7EB",
                        borderStyle: "solid",
                        borderWidth: 1,
                        "&:hover": { backgroundColor: alpha("#111927", 0.04) },
                      }}
                    >
                      <Grid
                        container
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        style={{ minHeight: "80px", padding: 20 }}
                      >
                        <Stack alignItems="center" direction="row" spacing={2}>
                          <CloudUploadIcon style={{ color: "gray" }} />
                          <Typography variant="body2" color="text.secondary">
                            {t("dragndrop")}
                          </Typography>
                        </Stack>
                      </Grid>
                    </Paper>
                  </Box>
                }
              />
              {fileSelected && (
                <Stack alignItems="center" direction="row" spacing={1}>
                  <AttachmentIcon style={{ color: "gray" }} />
                  <Typography variant="body2" color="text.secondary">
                    <b>{fileSelected.name}</b>
                  </Typography>
                </Stack>
              )}
            </Stack>
            {/* Description Field */}
            <Stack spacing={1}>
              <Typography color="text.secondary" variant="h6">
                {t("Description")}
              </Typography>
              <TextField
                fullWidth
                name="description"
                type="text"
                multiline
                rows={2}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </Stack>
            {/* File Uploader for Image */}
            <Stack spacing={1}>
              <Typography color="text.secondary" variant="h6">
                {t("Image")}
              </Typography>
              <FileUploader
                handleChange={handleImageChange}
                multiple={false}
                name="image"
                types={imageTypes}
                hoverTitle=" "
                maxSize={10}
                children={
                  <Box>
                    <Paper
                      variant="outlined"
                      sx={{
                        borderColor: "#E5E7EB",
                        borderStyle: "solid",
                        borderWidth: 1,
                        "&:hover": { backgroundColor: alpha("#111927", 0.04) },
                      }}
                    >
                      <Grid
                        container
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        style={{ minHeight: "80px", padding: 20 }}
                      >
                        <Stack alignItems="center" direction="row" spacing={2}>
                          <ImageIcon style={{ color: "gray" }} />
                          <Typography variant="body2" color="text.secondary">
                            {t("dragndrop")}
                          </Typography>
                        </Stack>
                      </Grid>
                    </Paper>
                  </Box>
                }
              />
              {imageSelected && (
                <Stack alignItems="center" direction="row" spacing={1}>
                  <AttachmentIcon style={{ color: "gray" }} />
                  <Typography variant="body2" color="text.secondary">
                    <b>{imageSelected.name}</b>
                  </Typography>
                </Stack>
              )}
            </Stack>
            {/* Processing Options using Controlled Tree View */}
            <Stack spacing={1}>
              <Typography color="text.secondary" variant="h6">
                {t("ProcessingOptions")}
              </Typography>
              <SimpleTreeView
                multiSelect
                checkboxSelection
                selectedItems={selectedItems}
                onSelectedItemsChange={handleSelectedItemsChange}
                expandedItems={expandedItems}
              >
                {processings.map((processing) => {
                  // Create a valid itemId from the title.
                  const parentId = createItemId(processing.title);
                  // If there are sub-options, render a parent node with children.
                  if (processing.subProcessings && processing.subProcessings.length > 0) {
                    return (
                      <TreeItem
                        key={processing.id}
                        itemId={parentId}
                        label={
                          <Box display="flex" alignItems="center">
                            <Typography variant="body2" color="text.secondary">{t(processing.title)}</Typography>
                            <IconButton
                              size="small"
                              onClick={e => {
                                e.stopPropagation();
                                handleOpenInfo(processing);
                              }}
                            >
                              <HelpOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        }
                      >
                        {processing.subProcessings.map((sub, index) => {
                          const subId = `${createItemId(sub.title)}`;
                          return (
                            <TreeItem
                              key={sub.id}
                              itemId={subId}
                              label={<Typography variant="body2" color="text.secondary">{t(sub.title)}</Typography>}
                            />
                          );
                        })}
                      </TreeItem>
                    );
                  }
                  // Otherwise, render a single node.
                  return (
                    <TreeItem
                      key={processing.id}
                      itemId={parentId}
                      label={
                        <Box display="flex" alignItems="center">
                          <Typography variant="body2" color="text.secondary">{t(processing.title)}</Typography>
                          <IconButton
                            size="small"
                            onClick={e => {
                              e.stopPropagation();
                              handleOpenInfo(processing);
                            }}
                          >
                            <HelpOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      }
                    />
                  );
                })}
              </SimpleTreeView>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={
              <SvgIcon fontSize="medium">
                <FileUploadIcon />
              </SvgIcon>
            }
            variant="contained"
            onClick={handleCreate}
          >
            {t("Upload")}
          </Button>
        </DialogActions>
      </Box>
      <ProcessingInfoDialog
        open={infoDialogOpen}
        onClose={handleCloseInfo}
        processing={currentProcessing}
      />
    </Dialog>
  );
}
